import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Avatar,
    Box,
    Breadcrumbs,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    Link as MuiLink,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    ArrowDropDown as ArrowDropDownIcon,
    ArrowDropUp as ArrowDropUpIcon,
    Refresh as RefreshIcon,
    ShoppingCart as ShoppingCartIcon,
    Visibility as VisibilityIcon,
    TrendingUp as TrendingUpIcon,
    SwapVert as SwapVertIcon,
    Campaign as CampaignIcon,
} from '@mui/icons-material';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid,
    Tooltip as RechartsTooltip, ResponsiveContainer, Legend, ReferenceLine,
} from 'recharts';
import axios from '../../utils/axios';
import { useNotify } from '../../hooks';
import DateRangeField from '../../components/ui/DateRangeField';
import { MP_MAP, MP_COLORS, MP_TEXT_COLORS, getWbImageUrl, toThumbnail, pickProductImage } from '../../utils/productImage';

const fmtYmd = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const fmtNum = (v, dec = 0) => {
    if (v == null || v === '') return '—';
    const n = Number(v);
    if (!Number.isFinite(n)) return '—';
    return n.toLocaleString('ru-RU', { minimumFractionDigits: dec, maximumFractionDigits: dec });
};
const fmtMoney = (v) => fmtNum(v, 2);
const fmtPct = (v) => (v == null || v === '' ? '—' : `${fmtNum(v, 2)}%`);

const calcDeltaPct = (curr, prev) => {
    if (prev == null || prev === 0 || curr == null) return null;
    return ((Number(curr) - Number(prev)) / Math.abs(Number(prev))) * 100;
};

const sumField = (rows, key) =>
    rows.reduce((acc, r) => acc + Number(r?.[key] || 0), 0);

const CHART_COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088fe', '#00C49F', '#FF8042', '#a4de6c'];

const METRIC_GROUP_COLORS = {
    traffic: '#e3f2fd',
    cart: '#fff3e0',
    orders: '#e8f5e9',
    advert: '#ffebee',
    finance: '#fff8e1',
};

const cellStyle = { py: 0.5, px: 1, fontSize: '0.8rem', whiteSpace: 'nowrap' };
const headerStyle = { ...cellStyle, fontWeight: 'bold', bgcolor: 'grey.100' };

// Метрики для транспонированной таблицы дневной аналитики
// agg: 'sum' — суммировать значения за период (Итого);
//      'avg' — среднее по дням (для процентов, рассчитываемых на бэке);
//      'derived' — пересчитать через формулу из суммированных полей (правильнее для CR/ROI/DRR).
const DAILY_METRICS = [
    { key: 'openCardCount', label: 'Клики/Показы', group: 'traffic', fmt: 'int', agg: 'sum' },
    { key: 'addToCartCount', label: 'В корзину', group: 'cart', fmt: 'int', agg: 'sum' },
    { key: 'addToCartConversion', label: 'CR корзины %', group: 'cart', fmt: 'pct', agg: 'derived',
        derive: (a) => a.openCardCount ? (a.addToCartCount / a.openCardCount) * 100 : null },
    { key: 'ordersCount', label: 'Заказов', group: 'orders', fmt: 'int', agg: 'sum' },
    { key: 'ordersSumRub', label: 'Сумма заказов', group: 'orders', fmt: 'money', agg: 'sum' },
    { key: 'avgPrice', label: 'Средняя цена', group: 'orders', fmt: 'money', agg: 'derived',
        derive: (a) => a.ordersCount ? a.ordersSumRub / a.ordersCount : null },
    { key: 'cartToOrderConversion', label: 'CR заказа %', group: 'orders', fmt: 'pct', agg: 'derived',
        derive: (a) => a.addToCartCount ? (a.ordersCount / a.addToCartCount) * 100 : null },
    { key: 'buyoutsCount', label: 'Выкупов', group: 'orders', fmt: 'dec2', agg: 'sum' },
    { key: 'buyoutPercent', label: '% выкупа', group: 'orders', fmt: 'pct', agg: 'avg' },
    { key: 'advViews', label: 'Реклама показы', group: 'advert', fmt: 'int', agg: 'sum' },
    { key: 'advClicks', label: 'Реклама клики', group: 'advert', fmt: 'int', agg: 'sum' },
    { key: 'advSum', label: 'Реклама расход', group: 'advert', fmt: 'money', agg: 'sum' },
    { key: 'advCpcSum', label: '↳ За клик', group: 'advert', fmt: 'money', agg: 'sum', mp: 2 },
    { key: 'advCpoSum', label: '↳ За заказ', group: 'advert', fmt: 'money', agg: 'sum', mp: 2 },
    { key: 'cost', label: 'Себестоимость', group: 'finance', fmt: 'money', agg: 'sum' },
    { key: 'comission', label: 'Комиссия', group: 'finance', fmt: 'money', agg: 'sum' },
    { key: 'logistic', label: 'Логистика', group: 'finance', fmt: 'money', agg: 'sum' },
    { key: 'profit', label: 'Профит', group: 'finance', fmt: 'money', agg: 'sum' },
];

// Метрики, у которых "меньше" = лучше
const LOWER_BETTER = new Set(['cost', 'comission', 'logistic', 'avg_position', 'avgPosition', 'advSum', 'advCpcSum', 'advCpoSum']);

const fmtMetric = (fmt, v) => {
    if (fmt === 'money') return fmtMoney(v);
    if (fmt === 'pct') return fmtPct(v);
    if (fmt === 'dec1') return fmtNum(v, 1);
    if (fmt === 'dec2') return fmtNum(v, 2);
    return fmtNum(v, 0);
};

function StatTile({ title, value, currentRaw, subtitle, prevValue, prevValueDisplay, prevSubtitle, icon, color = 'primary', lowerBetter = false, showSubtitle }) {
    const theme = useTheme();
    const colorMap = {
        primary: { bg: theme.palette.primary.light, text: theme.palette.primary.dark, icon: theme.palette.primary.main },
        success: { bg: theme.palette.success.light, text: theme.palette.success.dark, icon: theme.palette.success.main },
        info:    { bg: theme.palette.info.light,    text: theme.palette.info.dark,    icon: theme.palette.info.main },
        warning: { bg: theme.palette.warning.light, text: theme.palette.warning.dark, icon: theme.palette.warning.main },
        error:   { bg: theme.palette.error.light,   text: theme.palette.error.dark,   icon: theme.palette.error.main },
    };
    const c = colorMap[color] || colorMap.primary;

    // currentRaw — сырое числовое значение для расчёта дельты;
    // value может быть отформатированной строкой ("159 414") и не парсится через Number().
    const deltaPct = calcDeltaPct(currentRaw ?? value, prevValue);
    const isGood = deltaPct == null ? null : (lowerBetter ? deltaPct < 0 : deltaPct > 0);
    const Arrow = (deltaPct ?? 0) >= 0 ? ArrowDropUpIcon : ArrowDropDownIcon;
    const deltaColor = isGood == null ? 'text.secondary' : (isGood ? 'success.main' : 'error.main');

    const hasPrev = (prevValueDisplay != null && prevValueDisplay !== '') || (prevSubtitle != null && prevSubtitle !== '');
    const showSub = showSubtitle ?? !!subtitle;

    return (
        <Card sx={{ height: '100%', bgcolor: alpha(c.bg, 0.3), border: `1px solid ${alpha(c.icon, 0.2)}` }}>
            <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                    {icon && (
                        <Box
                            sx={{
                                width: 32, height: 32, borderRadius: 1.5, flexShrink: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                bgcolor: c.bg, color: c.text,
                            }}
                        >
                            {icon}
                        </Box>
                    )}
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', flex: 1, minWidth: 0 }} noWrap>
                        {title}
                    </Typography>
                    {deltaPct != null && Number.isFinite(deltaPct) && Math.abs(deltaPct) >= 0.01 && (
                        <Box sx={{
                            display: 'flex', alignItems: 'center', px: 0.75, py: 0.25, borderRadius: 1.5, flexShrink: 0,
                            bgcolor: alpha(isGood ? theme.palette.success.main : theme.palette.error.main, 0.12),
                        }}>
                            <Arrow sx={{ color: deltaColor, fontSize: 18, m: -0.25 }} />
                            <Typography variant="caption" sx={{ color: deltaColor, fontWeight: 600 }}>
                                {fmtNum(Math.abs(deltaPct), 1)}%
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
                        {value == null || value === '' ? '—' : value}
                    </Typography>
                    {showSub && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                            {subtitle ?? ''}
                        </Typography>
                    )}
                </Box>

                {hasPrev && (
                    <>
                        <Divider sx={{ my: 1, borderColor: alpha(c.icon, 0.25) }}>
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Было
                            </Typography>
                        </Divider>
                        {prevValueDisplay != null && prevValueDisplay !== '' && (
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', lineHeight: 1.2 }}>
                                {prevValueDisplay}
                            </Typography>
                        )}
                        {prevSubtitle != null && prevSubtitle !== '' && (
                            <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', mt: 0.25 }}>
                                {prevSubtitle}
                            </Typography>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
}

function MpChip({ mp, selected, onClick }) {
    const label = MP_MAP[mp] || String(mp);
    return (
        <Chip
            label={label}
            size="small"
            onClick={onClick}
            sx={{
                bgcolor: selected ? MP_COLORS[mp] : 'grey.200',
                color: selected ? MP_TEXT_COLORS[mp] : 'text.secondary',
                fontWeight: 600,
                cursor: 'pointer',
                border: selected ? `2px solid ${MP_COLORS[mp]}` : '2px solid transparent',
                '&:hover': { bgcolor: selected ? MP_COLORS[mp] : 'grey.300' },
            }}
        />
    );
}

export default function ProductDetails() {
    const { error: showErrorRaw } = useNotify();
    // useNotify возвращает новые arrow-функции на каждом рендере — стабилизируем через ref,
    // иначе useCallback'и в loaders пересоздаются и вызывают бесконечный цикл useEffect.
    const showErrorRef = useRef(showErrorRaw);
    showErrorRef.current = showErrorRaw;
    const showError = useCallback((msg) => showErrorRef.current?.(msg), []);

    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const podgruppa = searchParams.get('podgruppa') || '';
    const gruppa = searchParams.get('gruppa') || '';
    const article = searchParams.get('article') || '';
    const organization = searchParams.get('organization') || '';
    const mpParam = searchParams.get('mp') || '';

    const today = new Date();
    const minus30 = new Date(today);
    minus30.setDate(today.getDate() - 30);

    const [dateFrom, setDateFrom] = useState(fmtYmd(minus30));
    const [dateTo, setDateTo] = useState(fmtYmd(today));

    const [products, setProducts] = useState([]);
    const [productsLoading, setProductsLoading] = useState(false);

    const [dailyRows, setDailyRows] = useState([]);
    const [dailyPrevRows, setDailyPrevRows] = useState([]);
    const [dailyLoading, setDailyLoading] = useState(false);

    const [searchSummary, setSearchSummary] = useState({ current: {}, prev: {} });
    const [searchLoading, setSearchLoading] = useState(false);

    const [keywordsRows, setKeywordsRows] = useState([]);
    const [keywordsAvgByDay, setKeywordsAvgByDay] = useState([]);
    const [keywordsLoading, setKeywordsLoading] = useState(false);

    const [chartMetrics, setChartMetrics] = useState(['ordersCount']);

    const versionRef = useRef(0);

    const isWb = !mpParam || mpParam === '1';
    const isOzon = mpParam === '2';
    const visibleDailyMetrics = useMemo(
        () => DAILY_METRICS.filter((m) => m.mp == null || String(m.mp) === String(mpParam)),
        [mpParam],
    );

    // ===== Update URL params =====
    const updateParam = useCallback((key, value) => {
        const next = new URLSearchParams(searchParams);
        if (value == null || value === '') {
            next.delete(key);
        } else {
            next.set(key, String(value));
        }
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    // Тоггл «товара» — выбираем сразу по паре (article, organization),
    // потому что один offer_id может встречаться в нескольких организациях.
    const toggleProduct = useCallback((row) => {
        const isSame = article === row.offer_id && organization === row.organization;
        const next = new URLSearchParams(searchParams);
        if (isSame) {
            next.delete('article');
            next.delete('organization');
        } else {
            if (row.offer_id) next.set('article', row.offer_id); else next.delete('article');
            if (row.organization) next.set('organization', row.organization); else next.delete('organization');
        }
        setSearchParams(next, { replace: true });
    }, [article, organization, searchParams, setSearchParams]);

    const toggleMp = (mpVal) => {
        const v = String(mpVal);
        if (mpParam === v) updateParam('mp', '');
        else updateParam('mp', v);
    };

    // ===== Build common params =====
    const commonParams = useMemo(() => {
        const p = {};
        if (podgruppa) p.podgruppa = podgruppa;
        if (gruppa) p.gruppa = gruppa;
        if (article) p.article = article;
        if (organization) p.organization = organization;
        if (mpParam) p.mp = mpParam;
        return p;
    }, [podgruppa, gruppa, article, organization, mpParam]);

    // ===== Load products of subgroup =====
    const loadProducts = useCallback(async () => {
        if (!podgruppa) return;
        setProductsLoading(true);
        try {
            const params = { podgruppa, limit: 200 };
            if (gruppa) params.gruppa = gruppa;
            const resp = await axios.get('/products/list', { params });
            setProducts(resp.data.rows || []);
        } catch (e) {
            showError(e?.response?.data?.message || e?.message || 'Ошибка загрузки товаров');
        } finally {
            setProductsLoading(false);
        }
    }, [podgruppa, gruppa, showError]);

    useEffect(() => { loadProducts(); }, [loadProducts]);

    // Filtered products: by article + organization + mp (для отображения в шапке)
    const visibleProducts = useMemo(() => {
        let list = products;
        if (article) list = list.filter(p => p.offer_id === article);
        if (organization) list = list.filter(p => p.organization === organization);
        if (mpParam) {
            const mpNum = Number(mpParam);
            list = list.filter(p => Array.isArray(p.mp_list) && p.mp_list.includes(mpNum));
        }
        return list;
    }, [products, article, organization, mpParam]);

    // Все маркетплейсы, которые есть в подгруппе (для иконок-фильтров)
    const availableMps = useMemo(() => {
        const set = new Set();
        for (const p of products) {
            if (Array.isArray(p.mp_list)) p.mp_list.forEach(m => set.add(m));
        }
        return [...set].sort();
    }, [products]);

    // ===== Load daily statistic (current + previous period) =====
    const loadDaily = useCallback(async () => {
        const version = ++versionRef.current;
        if (!dateFrom || !dateTo) return;

        setDailyLoading(true);

        const d1 = new Date(dateFrom);
        const d2 = new Date(dateTo);
        const days = Math.round((d2 - d1) / 86400000) + 1;
        const prevEnd = new Date(d1); prevEnd.setDate(prevEnd.getDate() - 1);
        const prevStart = new Date(prevEnd); prevStart.setDate(prevStart.getDate() - days + 1);

        const baseParams = { ...commonParams };
        const currParams = { ...baseParams, date_from: dateFrom, date_to: dateTo };
        const prevParams = { ...baseParams, date_from: fmtYmd(prevStart), date_to: fmtYmd(prevEnd) };

        try {
            const [curr, prev] = await Promise.all([
                axios.get('/analytics/wb/statistic/daily', { params: currParams }),
                axios.get('/analytics/wb/statistic/daily', { params: prevParams }),
            ]);
            if (version === versionRef.current) {
                setDailyRows(curr.data.rows || []);
                setDailyPrevRows(prev.data.rows || []);
            }
        } catch (e) {
            if (version === versionRef.current) {
                showError(e?.response?.data?.message || e?.message || 'Ошибка загрузки аналитики');
            }
        } finally {
            if (version === versionRef.current) setDailyLoading(false);
        }
    }, [commonParams, dateFrom, dateTo, showError]);

    // ===== Load search summary (WB only) =====
    const loadSearchSummary = useCallback(async () => {
        if (!isWb) {
            setSearchSummary({ current: {}, prev: {} });
            return;
        }
        const version = versionRef.current;
        setSearchLoading(true);
        try {
            const params = { startDate: dateFrom, endDate: dateTo };
            if (podgruppa) params.podgruppa = podgruppa;
            if (gruppa) params.gruppa = gruppa;
            if (article) params.offer_id = article;
            const resp = await axios.get('/analytics/wb/search/summary', { params });
            if (version === versionRef.current) setSearchSummary(resp.data || { current: {}, prev: {} });
        } catch (e) {
            // 403 если нет analyticsSearch — это нормально, просто скрываем блок
            if (e?.response?.status !== 403) {
                showError(e?.response?.data?.message || e?.message || 'Ошибка загрузки поиска');
            }
        } finally {
            setSearchLoading(false);
        }
    }, [isWb, dateFrom, dateTo, podgruppa, gruppa, article, showError]);

    // ===== Load keywords-by-day matrix (WB only) =====
    const loadKeywords = useCallback(async () => {
        if (!isWb || !podgruppa) {
            setKeywordsRows([]);
            setKeywordsAvgByDay([]);
            return;
        }
        const version = versionRef.current;
        setKeywordsLoading(true);
        try {
            const params = { startDate: dateFrom, endDate: dateTo, podgruppa };
            if (gruppa) params.gruppa = gruppa;
            if (article) params.offer_id = article;
            const resp = await axios.get('/analytics/wb/search/keywords-by-day', { params });
            if (version === versionRef.current) {
                setKeywordsRows(resp.data.rows || []);
                setKeywordsAvgByDay(resp.data.avgByDay || []);
            }
        } catch (e) {
            if (e?.response?.status !== 403) {
                showError(e?.response?.data?.message || e?.message || 'Ошибка загрузки ключевых слов');
            }
            setKeywordsRows([]);
            setKeywordsAvgByDay([]);
        } finally {
            setKeywordsLoading(false);
        }
    }, [isWb, podgruppa, gruppa, article, dateFrom, dateTo, showError]);

    const reloadAll = useCallback(() => {
        loadDaily();
        loadSearchSummary();
        loadKeywords();
    }, [loadDaily, loadSearchSummary, loadKeywords]);

    useEffect(() => { reloadAll(); /* eslint-disable-next-line */ }, [commonParams, dateFrom, dateTo]);

    // ===== Aggregations for cards =====
    const aggCurr = useMemo(() => ({
        openCardCount: sumField(dailyRows, 'openCardCount'),
        addToCartCount: sumField(dailyRows, 'addToCartCount'),
        ordersCount: sumField(dailyRows, 'ordersCount'),
        ordersSumRub: sumField(dailyRows, 'ordersSumRub'),
        advViews: sumField(dailyRows, 'advViews'),
        advClicks: sumField(dailyRows, 'advClicks'),
        advSum: sumField(dailyRows, 'advSum'),
        advCpcSum: sumField(dailyRows, 'advCpcSum'),
        advCpoSum: sumField(dailyRows, 'advCpoSum'),
    }), [dailyRows]);

    const aggPrev = useMemo(() => ({
        openCardCount: sumField(dailyPrevRows, 'openCardCount'),
        addToCartCount: sumField(dailyPrevRows, 'addToCartCount'),
        ordersCount: sumField(dailyPrevRows, 'ordersCount'),
        ordersSumRub: sumField(dailyPrevRows, 'ordersSumRub'),
        advViews: sumField(dailyPrevRows, 'advViews'),
        advClicks: sumField(dailyPrevRows, 'advClicks'),
        advSum: sumField(dailyPrevRows, 'advSum'),
        advCpcSum: sumField(dailyPrevRows, 'advCpcSum'),
        advCpoSum: sumField(dailyPrevRows, 'advCpoSum'),
    }), [dailyPrevRows]);

    // CR = orders/openCardCount * 100 — на основе суммированных значений
    const crCartCurr = aggCurr.openCardCount ? (aggCurr.addToCartCount / aggCurr.openCardCount) * 100 : 0;
    const crCartPrev = aggPrev.openCardCount ? (aggPrev.addToCartCount / aggPrev.openCardCount) * 100 : 0;
    const crOrderCurr = aggCurr.addToCartCount ? (aggCurr.ordersCount / aggCurr.addToCartCount) * 100 : 0;
    const crOrderPrev = aggPrev.addToCartCount ? (aggPrev.ordersCount / aggPrev.addToCartCount) * 100 : 0;
    const drrCurr = aggCurr.ordersSumRub ? (aggCurr.advSum / aggCurr.ordersSumRub) * 100 : 0;
    const drrPrev = aggPrev.ordersSumRub ? (aggPrev.advSum / aggPrev.ordersSumRub) * 100 : 0;

    const sCurr = searchSummary.current || {};
    const sPrev = searchSummary.prev || {};
    // avg_position в summary не считается напрямую — там подсчёт топов; посчитаем через pivot? 
    // Для упрощения возьмём средневзвешенную долю top10 / total_products как "качество позиции",
    // а видимость — share top10 от total_products.
    const total = Number(sCurr.total_products || 0);
    const totalPrev = Number(sPrev.total_products || 0);
    const top10Curr = Number(sCurr.top10 || 0);
    const top10Prev = Number(sPrev.top10 || 0);
    const visHighCurr = Number(sCurr.vis_high || 0);
    const visHighPrev = Number(sPrev.vis_high || 0);

    const visibilityScoreCurr = total ? (top10Curr / total) * 100 : 0;
    const visibilityScorePrev = totalPrev ? (top10Prev / totalPrev) * 100 : 0;
    const highShareCurr = total ? (visHighCurr / total) * 100 : 0;
    const highSharePrev = totalPrev ? (visHighPrev / totalPrev) * 100 : 0;
    const avgPositionCurr = sCurr.avg_position != null ? Number(sCurr.avg_position) : null;
    const avgPositionPrev = sPrev.avg_position != null ? Number(sPrev.avg_position) : null;

    // ===== Price/category info =====
    const priceRange = useMemo(() => {
        const list = visibleProducts.map(p => Number(p.price)).filter(v => v > 0);
        if (!list.length) return null;
        const min = Math.min(...list);
        const max = Math.max(...list);
        return min === max ? `${fmtMoney(min)} ₽` : `${fmtMoney(min)} – ${fmtMoney(max)} ₽`;
    }, [visibleProducts]);

    const headerInfo = useMemo(() => {
        const first = visibleProducts[0] || products[0];
        if (!first) return null;
        return {
            category: first.category_name,
            description: first.content_name,
            brand: first.brand,
        };
    }, [visibleProducts, products]);

    // ===== Chart data =====
    const sortedDaily = useMemo(() => [...dailyRows].reverse(), [dailyRows]);

    // Агрегаты "Итого": сумма для int/money, среднее для процентов с бэка,
    // derived — корректный пересчёт CR/avgPrice из суммированных базовых полей.
    const periodAgg = useMemo(() => {
        if (!sortedDaily.length) return null;
        const sums = {};
        const counts = {};
        for (const m of DAILY_METRICS) {
            sums[m.key] = 0;
            counts[m.key] = 0;
        }
        for (const r of sortedDaily) {
            for (const m of DAILY_METRICS) {
                const v = Number(r[m.key]);
                if (Number.isFinite(v)) {
                    sums[m.key] += v;
                    counts[m.key] += 1;
                }
            }
        }
        const result = {};
        for (const m of DAILY_METRICS) {
            if (m.agg === 'derived' && typeof m.derive === 'function') {
                result[m.key] = m.derive(sums);
            } else if (m.agg === 'avg') {
                result[m.key] = counts[m.key] > 0 ? sums[m.key] / counts[m.key] : null;
            } else {
                result[m.key] = sums[m.key];
            }
        }
        return result;
    }, [sortedDaily]);

    const { chartData, chartRaw, chartDomain } = useMemo(() => {
        if (!sortedDaily.length || !chartMetrics.length) return { chartData: [], chartRaw: [], chartDomain: [80, 120] };
        const means = {};
        for (const k of chartMetrics) means[k] = 0;
        const raw = sortedDaily.map(r => {
            const point = { dt: (r.dt || '').slice(5) };
            for (const k of chartMetrics) {
                const v = Number(r[k] || 0);
                point[k] = v;
                means[k] += v;
            }
            return point;
        });
        const n = raw.length;
        for (const k of chartMetrics) means[k] = means[k] / n || 1;

        let globalMin = 100, globalMax = 100;
        const norm = raw.map(point => {
            const p = { dt: point.dt };
            for (const k of chartMetrics) {
                const pct = (point[k] / means[k]) * 100;
                p[k] = pct;
                if (pct < globalMin) globalMin = pct;
                if (pct > globalMax) globalMax = pct;
            }
            return p;
        });
        const pad = Math.max((globalMax - globalMin) * 0.15, 5);
        const domain = [Math.floor(globalMin - pad), Math.ceil(globalMax + pad)];
        return { chartData: norm, chartRaw: raw, chartDomain: domain };
    }, [sortedDaily, chartMetrics]);

    const metricLabel = (key) => DAILY_METRICS.find(m => m.key === key)?.label || key;
    const toggleChartMetric = (key) =>
        setChartMetrics(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);

    const ChartTooltipContent = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null;
        const idx = chartData.findIndex(d => d.dt === label);
        const rawPoint = idx >= 0 ? chartRaw[idx] : null;
        return (
            <Paper sx={{ p: 1, fontSize: '0.8rem' }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>{`Дата: ${label}`}</Typography>
                {payload.map((entry) => {
                    const m = DAILY_METRICS.find(mm => mm.key === entry.dataKey);
                    const fmt = m?.fmt || 'int';
                    const v = rawPoint?.[entry.dataKey] ?? entry.value;
                    return (
                        <Box key={entry.dataKey} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: entry.color, flexShrink: 0 }} />
                            <span>{entry.name}: <b>{fmtMetric(fmt, v)}</b></span>
                        </Box>
                    );
                })}
            </Paper>
        );
    };

    // ===== Keywords matrix transform =====
    const keywordsMatrix = useMemo(() => {
        const dateSet = new Set();
        const map = new Map(); // text -> { dt -> {avg_position, position_mpstats, ad_position_mpstats, organic_position_mpstats, frequency, orders} }
        const totals = new Map(); // text -> total frequency for sort
        for (const r of keywordsRows) {
            dateSet.add(r.dt);
            if (!map.has(r.text)) map.set(r.text, {});
            map.get(r.text)[r.dt] = {
                avg_position: r.avg_position,
                position_mpstats: r.position_mpstats,
                ad_position_mpstats: r.ad_position_mpstats,
                organic_position_mpstats: r.organic_position_mpstats,
                frequency: Number(r.frequency || 0),
                orders: Number(r.orders || 0),
            };
            totals.set(r.text, (totals.get(r.text) || 0) + Number(r.frequency || 0));
        }
        // Подмешиваем даты из avgByDay (на случай, если по ключам данных нет, но средние есть)
        for (const a of keywordsAvgByDay) dateSet.add(a.dt);

        const dates = [...dateSet].sort();
        const rows = [...map.entries()]
            .map(([text, byDt]) => ({ text, byDt, total: totals.get(text) || 0 }))
            .sort((a, b) => b.total - a.total)
            .slice(0, 50);

        // Сводная "среднее по подгруппе" строка
        const avgRow = keywordsAvgByDay.length
            ? {
                text: 'Среднее по подгруппе',
                isAverage: true,
                byDt: Object.fromEntries(keywordsAvgByDay.map(a => [a.dt, {
                    avg_position: a.avg_position,
                    position_mpstats: a.position_mpstats,
                    ad_position_mpstats: a.ad_position_mpstats,
                    organic_position_mpstats: a.organic_position_mpstats,
                    frequency: Number(a.frequency || 0),
                    orders: Number(a.orders || 0),
                    keywords_count: Number(a.keywords_count || 0),
                }])),
                total: keywordsAvgByDay.reduce((s, a) => s + Number(a.frequency || 0), 0),
            }
            : null;

        return { dates, rows, avgRow };
    }, [keywordsRows, keywordsAvgByDay]);

    const renderHeader = () => (
        <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <IconButton size="small" onClick={() => navigate('/analytics/products')}>
                    <ArrowBackIcon />
                </IconButton>
                <Breadcrumbs sx={{ fontSize: '0.85rem' }}>
                    <MuiLink component="button" onClick={() => navigate('/analytics/products')} underline="hover">Товары</MuiLink>
                    {gruppa && <Typography variant="body2" color="text.secondary">{gruppa}</Typography>}
                    <Typography variant="body2" color="text.primary" fontWeight={600}>{podgruppa || '—'}</Typography>
                </Breadcrumbs>
            </Stack>

            <Typography variant="h4" fontWeight="bold" gutterBottom>
                {podgruppa || 'Подгруппа не указана'}
            </Typography>

            <Stack direction="row" spacing={3} flexWrap="wrap" sx={{ mb: 1 }}>
                {headerInfo?.brand && (
                    <Box>
                        <Typography variant="caption" color="text.secondary">Бренд</Typography>
                        <Typography variant="body2" fontWeight={600}>{headerInfo.brand}</Typography>
                    </Box>
                )}
                {headerInfo?.category && (
                    <Box>
                        <Typography variant="caption" color="text.secondary">Категория</Typography>
                        <Typography variant="body2" fontWeight={600}>{headerInfo.category}</Typography>
                    </Box>
                )}
                {priceRange && (
                    <Box>
                        <Typography variant="caption" color="text.secondary">Цена</Typography>
                        <Typography variant="body2" fontWeight={600}>{priceRange}</Typography>
                    </Box>
                )}
                <Box>
                    <Typography variant="caption" color="text.secondary">Товаров</Typography>
                    <Typography variant="body2" fontWeight={600}>{products.length}</Typography>
                </Box>
            </Stack>

            {headerInfo?.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 900 }}>
                    {headerInfo.description}
                </Typography>
            )}
        </Paper>
    );

    const renderFilters = () => (
        <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="flex-start">
                {/* Photos */}
                <Grid item xs={12} md={8}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                        Цвета {productsLoading && <CircularProgress size={10} sx={{ ml: 1 }} />}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {products.length === 0 && !productsLoading && (
                            <Typography variant="body2" color="text.secondary">Нет товаров</Typography>
                        )}
                        {products.map((p) => {
                            const img = pickProductImage(p);
                            const noFilter = !article && !organization;
                            const isActive = article === p.offer_id && organization === p.organization;
                            const isSelected = noFilter || isActive;
                            const tooltipTitle = `${p.offer_id || ''}${p.organization ? ' · ' + p.organization : ''}${p.content_name ? ' · ' + p.content_name : ''}`;
                            return (
                                <Tooltip
                                    key={`${p.organization}-${p.barcode || p.offer_id}`}
                                    title={tooltipTitle}
                                    placement="top"
                                    arrow
                                >
                                    <Box
                                        onClick={() => toggleProduct(p)}
                                        sx={{
                                            position: 'relative',
                                            cursor: 'pointer',
                                            borderRadius: 1.5,
                                            border: `3px solid ${isActive ? '#1976d2' : 'transparent'}`,
                                            opacity: isSelected ? 1 : 0.35,
                                            transition: 'opacity 0.15s, border-color 0.15s, transform 0.15s',
                                            '&:hover': { transform: 'scale(1.05)' },
                                        }}
                                    >
                                        {img ? (
                                            <Avatar variant="rounded" src={img} sx={{ width: 56, height: 56 }} imgProps={{ loading: 'lazy' }}>
                                                {p.offer_id?.slice(0, 2) || '—'}
                                            </Avatar>
                                        ) : (
                                            <Avatar variant="rounded" sx={{ width: 56, height: 56, bgcolor: 'grey.200', color: 'grey.500', fontSize: 12 }}>
                                                {p.offer_id?.slice(0, 2) || '—'}
                                            </Avatar>
                                        )}
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                position: 'absolute', bottom: -16, left: 0, right: 0,
                                                textAlign: 'center', fontSize: '0.65rem', color: 'text.secondary',
                                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {p.offer_id}
                                        </Typography>
                                    </Box>
                                </Tooltip>
                            );
                        })}
                    </Box>
                </Grid>

                {/* MP filter */}
                <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                        Маркетплейс
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        {availableMps.map(mp => (
                            <MpChip key={mp} mp={mp} selected={!mpParam || mpParam === String(mp)} onClick={() => toggleMp(mp)} />
                        ))}
                        {mpParam && (
                            <Chip label="Сбросить" size="small" variant="outlined" onClick={() => updateParam('mp', '')} />
                        )}
                    </Stack>

                    <DateRangeField
                        valueFrom={dateFrom}
                        valueTo={dateTo}
                        onChange={({ from, to }) => { setDateFrom(from); setDateTo(to); }}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Button
                        variant="contained" size="small" onClick={reloadAll} disabled={dailyLoading}
                        startIcon={dailyLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                    >
                        Обновить
                    </Button>
                </Grid>
            </Grid>
        </Paper>
    );

    const renderCards = () => (
        <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3} lg={2.4}>
                <StatTile
                    title="Заказы"
                    value={fmtNum(aggCurr.ordersCount)}
                    currentRaw={aggCurr.ordersCount}
                    subtitle={`${fmtMoney(aggCurr.ordersSumRub)} ₽`}
                    showSubtitle
                    prevValue={aggPrev.ordersCount}
                    prevValueDisplay={fmtNum(aggPrev.ordersCount)}
                    prevSubtitle={`${fmtMoney(aggPrev.ordersSumRub)} ₽`}
                    icon={<ShoppingCartIcon />}
                    color="success"
                />
            </Grid>
            <Grid item xs={12} sm={6} md={3} lg={2.4}>
                <StatTile
                    title="Трафик"
                    value={fmtNum(aggCurr.openCardCount)}
                    currentRaw={aggCurr.openCardCount}
                    subtitle={isWb ? 'Клики WB' : 'Показы / клики'}
                    showSubtitle
                    prevValue={aggPrev.openCardCount}
                    prevValueDisplay={fmtNum(aggPrev.openCardCount)}
                    icon={<VisibilityIcon />}
                    color="info"
                />
            </Grid>
            <Grid item xs={12} sm={6} md={3} lg={2.4}>
                <StatTile
                    title="CR в корзину"
                    value={fmtPct(crCartCurr)}
                    currentRaw={crCartCurr}
                    subtitle={`Заказ CR: ${fmtPct(crOrderCurr)}`}
                    showSubtitle
                    prevValue={crCartPrev}
                    prevValueDisplay={fmtPct(crCartPrev)}
                    prevSubtitle={`Заказ CR: ${fmtPct(crOrderPrev)}`}
                    icon={<TrendingUpIcon />}
                    color="primary"
                />
            </Grid>
            <Grid item xs={12} sm={6} md={3} lg={2.4}>
                <StatTile
                    title="Реклама"
                    value={`${fmtMoney(aggCurr.advSum)} ₽`}
                    currentRaw={aggCurr.advSum}
                    subtitle={isOzon
                        ? `ДРР ${fmtPct(drrCurr)} · за клик ${fmtMoney(aggCurr.advCpcSum)} · за заказ ${fmtMoney(aggCurr.advCpoSum)}`
                        : `ДРР ${fmtPct(drrCurr)} · ${fmtNum(aggCurr.advClicks)} кликов`}
                    showSubtitle
                    prevValue={aggPrev.advSum}
                    prevValueDisplay={`${fmtMoney(aggPrev.advSum)} ₽`}
                    prevSubtitle={isOzon
                        ? `ДРР ${fmtPct(drrPrev)} · за клик ${fmtMoney(aggPrev.advCpcSum)} · за заказ ${fmtMoney(aggPrev.advCpoSum)}`
                        : `ДРР ${fmtPct(drrPrev)} · ${fmtNum(aggPrev.advClicks)} кликов`}
                    lowerBetter
                    icon={<CampaignIcon />}
                    color="warning"
                />
            </Grid>
            {isWb && (
                <Grid item xs={12} sm={6} md={3} lg={2.4}>
                    <StatTile
                        title="Средняя позиция"
                        value={avgPositionCurr != null ? fmtNum(avgPositionCurr, 1) : '—'}
                        currentRaw={avgPositionCurr}
                        subtitle={`Доля в ТОП-10: ${fmtPct(visibilityScoreCurr)}`}
                        showSubtitle
                        prevValue={avgPositionPrev}
                        prevValueDisplay={avgPositionPrev != null ? fmtNum(avgPositionPrev, 1) : null}
                        prevSubtitle={`Доля в ТОП-10: ${fmtPct(visibilityScorePrev)}`}
                        lowerBetter
                        icon={<SwapVertIcon />}
                        color="error"
                    />
                </Grid>
            )}
        </Grid>
    );

    const renderChart = () => (
        <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2">График динамики (норм. к среднему = 100%)</Typography>
                {chartMetrics.map((k, i) => (
                    <Chip
                        key={k}
                        label={metricLabel(k)}
                        size="small"
                        sx={{ borderLeft: `4px solid ${CHART_COLORS[i % CHART_COLORS.length]}` }}
                        onDelete={() => toggleChartMetric(k)}
                    />
                ))}
                {chartMetrics.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        Кликните по строке таблицы ниже, чтобы добавить показатель
                    </Typography>
                )}
                {chartMetrics.length > 1 && (
                    <Chip label="Сбросить" size="small" variant="outlined" onClick={() => setChartMetrics([])} />
                )}
            </Box>
            <Box sx={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                        <defs>
                            {chartMetrics.map((k, i) => (
                                <linearGradient key={k} id={`gradPd_${k}`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={CHART_COLORS[i % CHART_COLORS.length]} stopOpacity={0.25} />
                                    <stop offset="95%" stopColor={CHART_COLORS[i % CHART_COLORS.length]} stopOpacity={0} />
                                </linearGradient>
                            ))}
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="dt" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 11 }} width={40} domain={chartDomain} tickFormatter={v => `${v}%`} />
                        {chartMetrics.length > 0 && (
                            <ReferenceLine y={100} stroke="#999" strokeDasharray="4 4" />
                        )}
                        <RechartsTooltip content={<ChartTooltipContent />} />
                        <Legend />
                        {chartMetrics.map((k, i) => (
                            <Area
                                key={k}
                                type="monotone"
                                dataKey={k}
                                name={metricLabel(k)}
                                stroke={CHART_COLORS[i % CHART_COLORS.length]}
                                strokeWidth={2}
                                fill={`url(#gradPd_${k})`}
                            />
                        ))}
                    </AreaChart>
                </ResponsiveContainer>
            </Box>
        </Paper>
    );

    const renderDailyTable = () => {
        if (!sortedDaily.length) {
            return (
                <Paper sx={{ p: 4, mb: 2, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                        {dailyLoading ? <CircularProgress size={24} /> : 'Нет данных за выбранный период'}
                    </Typography>
                </Paper>
            );
        }
        let lastGroup = null;
        return (
            <Paper sx={{ mb: 2, overflow: 'hidden' }}>
                <Box sx={{ p: 1.5 }}>
                    <Typography variant="subtitle2">Дневная аналитика (показатели × даты)</Typography>
                </Box>
                <TableContainer sx={{ maxHeight: 'calc(100vh - 200px)' }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ ...headerStyle, position: 'sticky', left: 0, zIndex: 2 }}>Показатель</TableCell>
                                {sortedDaily.map(r => (
                                    <TableCell key={r.dt} sx={headerStyle} align="right">{r.dt}</TableCell>
                                ))}
                                <TableCell sx={{ ...headerStyle, fontWeight: 'bold' }} align="right">Итого</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleDailyMetrics.map((m) => {
                                const isFirstInGroup = m.group !== lastGroup;
                                lastGroup = m.group;
                                const groupColor = METRIC_GROUP_COLORS[m.group] || 'white';
                                const onChart = chartMetrics.includes(m.key);
                                const chartIdx = chartMetrics.indexOf(m.key);
                                const chartColor = onChart ? CHART_COLORS[chartIdx % CHART_COLORS.length] : null;
                                return (
                                    <TableRow
                                        key={m.key}
                                        hover
                                        sx={{
                                            cursor: 'pointer',
                                            borderTop: isFirstInGroup ? '2px solid #bdbdbd' : 'none',
                                            ...(onChart && { bgcolor: '#f0f0ff' }),
                                        }}
                                        onClick={() => toggleChartMetric(m.key)}
                                    >
                                        <TableCell
                                            sx={{
                                                ...cellStyle,
                                                position: 'sticky',
                                                left: 0,
                                                zIndex: 1,
                                                bgcolor: onChart ? '#e8e8ff' : (isFirstInGroup ? groupColor : '#fff'),
                                                fontWeight: 500,
                                                borderLeft: onChart ? `4px solid ${chartColor}` : `4px solid ${groupColor}`,
                                            }}
                                        >
                                            {m.label}
                                        </TableCell>
                                        {sortedDaily.map((r, idx) => {
                                            const prev = sortedDaily[idx - 1]?.[m.key];
                                            const cur = r[m.key];
                                            const dPct = calcDeltaPct(cur, prev);
                                            const lower = LOWER_BETTER.has(m.key);
                                            const isGood = dPct == null ? null : (lower ? dPct < 0 : dPct > 0);
                                            const Arrow = (dPct ?? 0) >= 0 ? ArrowDropUpIcon : ArrowDropDownIcon;
                                            const dColor = isGood == null ? 'text.secondary' : (isGood ? 'success.main' : 'error.main');
                                            return (
                                                <TableCell
                                                    key={`${r.dt}-${m.key}`}
                                                    sx={{ ...cellStyle, bgcolor: isFirstInGroup ? groupColor : 'inherit' }}
                                                    align="right"
                                                >
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                                        <span>{fmtMetric(m.fmt, cur)}</span>
                                                        {dPct != null && Number.isFinite(dPct) && Math.abs(dPct) >= 0.01 && (
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <Arrow sx={{ color: dColor, fontSize: 14, m: -0.25 }} />
                                                                <Typography variant="caption" sx={{ color: dColor, fontSize: '0.65rem', lineHeight: 1 }}>
                                                                    {fmtNum(Math.abs(dPct), 1)}%
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                            );
                                        })}
                                        <TableCell
                                            sx={{
                                                ...cellStyle,
                                                fontWeight: 'bold',
                                                bgcolor: isFirstInGroup ? groupColor : 'grey.100',
                                            }}
                                            align="right"
                                        >
                                            {fmtMetric(m.fmt, periodAgg ? periodAgg[m.key] : null)}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        );
    };

    const renderKeywords = () => {
        if (!isWb) return null;
        const { dates, rows, avgRow } = keywordsMatrix;
        const hasData = rows.length > 0 || !!avgRow;

        const renderKeywordRow = (row, opts = {}) => {
            const { isAverage = false } = opts;
            // Непрозрачные цвета — иначе при горизонтальной прокрутке через sticky-колонку
            // просвечивают цифры из других ячеек (alpha-фон не закрывает их полностью).
            const rowBg = isAverage ? '#eef3fb' : '#fff';
            const stickyBg = isAverage ? '#e3ecf8' : '#fff';
            return (
                <TableRow key={row.text} hover={!isAverage} sx={isAverage ? { bgcolor: rowBg } : undefined}>
                    <TableCell sx={{
                        ...cellStyle,
                        position: 'sticky', left: 0, zIndex: 1,
                        bgcolor: stickyBg,
                        fontWeight: isAverage ? 700 : 500,
                        whiteSpace: 'normal',
                        borderLeft: isAverage ? '4px solid #1976d2' : undefined,
                    }}>
                        {row.text}
                    </TableCell>
                    <TableCell sx={{ ...cellStyle, bgcolor: isAverage ? rowBg : undefined }} align="right">
                        <Typography variant="caption" color="text.secondary">{fmtNum(row.total)}</Typography>
                    </TableCell>
                    {dates.map((d, idx) => {
                        const cell = row.byDt[d];
                        const prevDate = dates[idx - 1];
                        const prev = prevDate ? row.byDt[prevDate]?.avg_position : null;
                        const cur = cell?.avg_position;
                        const delta = (cur != null && prev != null) ? (Number(cur) - Number(prev)) : null;
                        const isGood = delta != null ? delta < 0 : null;
                        const dColor = isGood == null ? 'text.secondary' : (isGood ? 'success.main' : 'error.main');
                        const mPos = cell?.position_mpstats;
                        const mAd = cell?.ad_position_mpstats;
                        const mOrg = cell?.organic_position_mpstats;
                        const hasMpstats = mPos != null || mAd != null || mOrg != null;
                        const fmtP = (v) => (v == null ? '—' : fmtNum(v, 0));
                        const isEmpty = cur == null && !hasMpstats;
                        return (
                            <TableCell key={d} sx={{ ...cellStyle, bgcolor: isAverage ? rowBg : undefined }} align="right">
                                {isEmpty ? (
                                    <Typography variant="caption" color="text.disabled">—</Typography>
                                ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                        {cur != null ? (
                                            <span style={{ fontWeight: isAverage ? 700 : 400 }}>{fmtNum(cur, 1)}</span>
                                        ) : (
                                            <Typography variant="caption" color="text.disabled">WB —</Typography>
                                        )}
                                        {delta != null && Math.abs(delta) >= 0.05 && (
                                            <Typography variant="caption" sx={{ color: dColor, fontSize: '0.65rem', lineHeight: 1 }}>
                                                {delta > 0 ? '+' : ''}{fmtNum(delta, 1)}
                                            </Typography>
                                        )}
                                        {hasMpstats && (
                                            <Tooltip
                                                title={
                                                    <Box sx={{ fontSize: '0.75rem' }}>
                                                        <div>MPSTATS позиции:</div>
                                                        <div>Общая: {fmtP(mPos)}</div>
                                                        <div>Реклама: {fmtP(mAd)}</div>
                                                        <div>Органика: {fmtP(mOrg)}</div>
                                                    </Box>
                                                }
                                                arrow
                                            >
                                                <Typography
                                                    variant="caption"
                                                    sx={{ color: 'primary.main', fontSize: '0.6rem', lineHeight: 1.1, cursor: 'help' }}
                                                >
                                                    {`M:${fmtP(mPos)} / Р:${fmtP(mAd)} / О:${fmtP(mOrg)}`}
                                                </Typography>
                                            </Tooltip>
                                        )}
                                    </Box>
                                )}
                            </TableCell>
                        );
                    })}
                </TableRow>
            );
        };

        return (
            <Paper sx={{ mb: 2, overflow: 'hidden' }}>
                <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle2">Поисковые запросы (позиция по дням)</Typography>
                    {keywordsLoading && <CircularProgress size={14} />}
                    {rows.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                            Топ-{rows.length} по частоте
                        </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                        Большое число — позиция WB · мелким: M общая / Р реклама / О органика (MPSTATS)
                    </Typography>
                </Box>
                {!hasData && !keywordsLoading ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Нет данных по поисковым запросам</Typography>
                    </Box>
                ) : (
                    <TableContainer sx={{ maxHeight: 600 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ ...headerStyle, position: 'sticky', left: 0, zIndex: 2, minWidth: 240 }}>Ключ</TableCell>
                                    <TableCell sx={headerStyle} align="right">Частота</TableCell>
                                    {dates.map(d => (
                                        <TableCell key={d} sx={headerStyle} align="right">{d.slice(5)}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {avgRow && renderKeywordRow(avgRow, { isAverage: true })}
                                {rows.map((row) => renderKeywordRow(row))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        );
    };

    return (
        <Box>
            {renderHeader()}
            {renderFilters()}
            {renderCards()}
            {renderChart()}
            {renderDailyTable()}
            {renderKeywords()}
        </Box>
    );
}
